import React from 'react';
import { ArrowRightIcon, ShieldCheckIcon, ExternalLinkIcon } from 'lucide-react';
import { SECURITY_CONTROLS } from '../data/platform';
import { BOOK_DEMO_URL, PRIVACY_URL, TERMS_URL } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { HumanControl } from '../components/platform/HumanControl';
import { ProcessCta } from '../components/platform/ProcessCta';
export function Security() {
  usePageMeta({
    title: 'Security | DiligenceGPT™',
    description: 'How DiligenceGPT™ handles sensitive diligence: workspace controls, role based permissions, tenant isolation, no training on customer deal data, human review and SOC 2 audit in progress.',
    path: '/security',
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Security',
        item: canonicalUrl('/security')
      }]
    }]
  });
  return <main id="main" data-id="element-2023" data-mp="1gla79obquylt">
      <PageHero headingId="security-page-heading" breadcrumb="Security" eyebrow="Security" title="Designed for Sensitive Decisions." description="Due process involves confidential deals, vendors and applicants. Here is how DiligenceGPT handles that information today." actions={<CtaButton href={BOOK_DEMO_URL} variant="violet" size="xl" event="enterprise_demo_click" eventParams={{
      placement: 'security_hero'
    }} className="w-full sm:w-auto" data-id="element-2025" data-mp="1zgrypqb111ro">
            Discuss your requirements
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2026" data-mp="2wy8s8jqgdhm2" />
          </CtaButton>} data-id="element-2024" data-mp="1lhs84d3yecsv" />

      <section aria-labelledby="controls-heading" className="border-b border-hairline-soft bg-surface" data-id="element-2027" data-mp="36s4ibkeajya1">
        <div className="mx-auto w-full max-w-[1100px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-2028" data-mp="31ve4z4tvfgsb">
          <SectionHeading eyebrow="Controls" headingId="controls-heading" title="How information is protected." data-id="element-2029" data-mp="2wtyhl6480y6r" />
          <ul className="mt-12 grid gap-4 sm:grid-cols-2" data-id="element-2030" data-mp="2ssv4ushgzi9r">
            {SECURITY_CONTROLS.map((control, index) => <Reveal key={control.label} as="li" delay={index % 2 * 0.04} className="flex gap-4 rounded-2xl border border-hairline bg-panel p-6" data-id="element-2031" data-mp="2nwegpya9t3wo" data-mpi={index}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-soft text-lime" data-id="element-2032" data-mp="2e3qntemevk38">
                  <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2033" data-mp="296qij5xdyld6" />
                </span>
                <div data-id="element-2034" data-mp="252irh23ggtbs">
                  <h3 className="text-base font-semibold text-text-primary" data-id="element-2035" data-mp="255wr42kqiofj">{control.label}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-text-secondary" data-id="element-2036" data-mp="205iqv6pdkis2">{control.body}</p>
                </div>
              </Reveal>)}
          </ul>

          <Reveal className="mt-10 rounded-2xl border border-hairline bg-panel p-6 sm:p-8" data-id="element-2037" data-mp="kkgiuuqm1wi4">
            <h2 className="text-lg font-semibold text-text-primary" data-id="element-2038" data-mp="oofy7a05mp9w">Policies</h2>
            <p className="mt-2 text-sm leading-relaxed text-text-secondary" data-id="element-2039" data-mp="tmzht3r9zoz3">
              Data handling is governed by our published policies. For specific security questionnaires or requirements, book a call with our team.
            </p>
            <div className="mt-5 flex flex-wrap gap-3" data-id="element-2040" data-mp="y22n9id28ak">
              {[{
              label: 'Privacy Policy',
              href: PRIVACY_URL
            }, {
              label: 'Terms',
              href: TERMS_URL
            }].map(link => <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-hairline px-4 text-sm font-semibold text-text-primary hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-2041" data-mp="apgb46saiifu">
                  {link.label}
                  <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2042" data-mp="1tn2zsghhho9b" />
                </a>)}
            </div>
          </Reveal>
        </div>
      </section>

      <HumanControl placement="security" data-id="element-2043" data-mp="23jegrjs0r2a3" />
      <ProcessCta placement="security" data-id="element-2044" data-mp="23mk52wstq04r" />
    </main>;
}